/**
 * Theme store — light / dark / system, persisted per-device.
 *
 * Three pieces of state cooperate:
 *
 *   1. **User preference** (`light` | `dark` | `system`) — what the user
 *      explicitly picked. Lives in `localStorage.flint_theme`. `system`
 *      means "follow the OS"; the default for a fresh device.
 *
 *   2. **OS preference** — what `prefers-color-scheme` reports. Re-evaluated
 *      whenever the OS flips, but only consulted while preference == `system`.
 *
 *   3. **Resolved theme** (`light` | `dark`) — the materialised result that
 *      flips the `.dark` class on `<html>`. This is what utilities resolve
 *      against (see `tokens.css :root.dark`).
 *
 * Persistence is per-device on purpose: people often want dark on their
 * phone and light on their laptop. A future user-preferences table can layer
 * on top without changing this module's contract — set the localStorage
 * value from a server-fetched preference at mount, done.
 *
 * The `.dark` class is applied **before React paints** by the inline script
 * in `apps/web/index.html`; this hook keeps the DOM in sync afterwards as
 * the user changes preference or the OS flips.
 */
import * as React from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "flint_theme";
const DARK_CLASS = "dark";

/**
 * Read the persisted preference from localStorage. Returns `'system'` if
 * nothing is stored or the value is malformed (defensive — older builds may
 * have stored other shapes).
 */
export function readPreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    /* localStorage may throw in private mode / sandboxed iframes */
  }
  return "system";
}

/** Read the OS-level preference once. Cheap; no listener attached here. */
export function readSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Resolve a preference into a concrete theme. */
export function resolveTheme(preference: ThemePreference, system: ResolvedTheme): ResolvedTheme {
  return preference === "system" ? system : preference;
}

/**
 * Apply the resolved theme to `<html>`. Idempotent — the inline boot script
 * may have already done this; re-applying is a no-op when the class is
 * already in the desired state.
 */
function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add(DARK_CLASS);
  else root.classList.remove(DARK_CLASS);
}

export interface UseThemeResult {
  /** What the user picked. */
  preference: ThemePreference;
  /** What is currently rendered (preference, resolved against the OS). */
  resolved: ResolvedTheme;
  /** Persist a new preference and apply it immediately. */
  setPreference: (next: ThemePreference) => void;
  /** Cycle light → dark → system → light (used by the topbar quick-toggle). */
  cyclePreference: () => void;
}

/**
 * Single source of truth for theme state inside React. Subscribes to OS
 * changes and to cross-tab `storage` events so the app stays consistent if
 * the user flips theme in another tab.
 */
export function useTheme(): UseThemeResult {
  const [preference, setPreferenceState] = React.useState<ThemePreference>(() => readPreference());
  const [systemTheme, setSystemTheme] = React.useState<ResolvedTheme>(() => readSystemTheme());

  // Watch the OS preference. We only USE it when preference == 'system',
  // but the state stays current so flipping back to 'system' picks up the
  // latest OS value without a roundtrip.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? "dark" : "light");
    };
    // `addEventListener` is the modern API; `addListener` is the Safari < 14
    // fallback (still in the wild on some MSE devices).
    if ("addEventListener" in mql) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    // @ts-expect-error legacy API
    mql.addListener(onChange);
    // @ts-expect-error legacy API
    return () => mql.removeListener(onChange);
  }, []);

  // Cross-tab sync — `storage` fires on OTHER tabs when localStorage changes.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const next = event.newValue;
      if (next === "light" || next === "dark" || next === "system") {
        setPreferenceState(next);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const resolved = resolveTheme(preference, systemTheme);

  // Apply on every render where the resolved theme can change. `applyTheme`
  // is idempotent so this is safe — and the inline boot script handled the
  // first paint before React even mounted.
  React.useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setPreference = React.useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* swallow — preference becomes session-only on locked-down browsers */
    }
  }, []);

  const cyclePreference = React.useCallback(() => {
    setPreferenceState((current) => {
      const next: ThemePreference =
        current === "light" ? "dark" : current === "dark" ? "system" : "light";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* swallow */
      }
      return next;
    });
  }, []);

  return { preference, resolved, setPreference, cyclePreference };
}
